import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';

import { BROWSE_SEARCH_PAGE_PATH } from 'app/constants/BrowseConstants';
import {
  BROWSE_CONFIG_ELEMENT_TYPE,
  DEPRECATED_LIST_ELEMENT_CONFIG_TYPE,
  ROW_CHILD_COMPONENT_TYPE,
  UI_CONTAINER_TYPE_BY_BROWSE_CONFIG_ELEMENT_TYPE
} from 'app/constants/browse/rows';
import { mapSearchBibToDisplayBib } from 'app/helpers/displayBib';

const mapLinkUrl = url => {
  if (!url) return null;

  const mappedUrl = /\.bibliocommons.com\/v2\/search/i.test(url)
    ? url.replace('/v2/search', BROWSE_SEARCH_PAGE_PATH)
    : url;

  try {
    // eslint-disable-next-line no-new
    new URL(mappedUrl);
    return mappedUrl;
  } catch {
    return null;
  }
};

const mapBibElementConfigToBibsRowProps = (
  bibConfig,
  { hasHistoryRoutingForBibTitles = false, searchBibByMetadataId }
) => {
  const { metadataIds = [], title, url: searchUrl } = bibConfig;

  const browseSearchUrl = searchUrl.replace('/v2/search', BROWSE_SEARCH_PAGE_PATH);

  let titleUrl;
  try {
    titleUrl = new URL(browseSearchUrl);
  } catch {
    return null;
  }
  if (!titleUrl.searchParams.get('title')) {
    titleUrl.searchParams.append('title', title);
  }

  const displayBibs = metadataIds
    .map(metadataId => {
      const searchBib = searchBibByMetadataId[metadataId];
      if (!searchBib) {
        return null;
      }

      return mapSearchBibToDisplayBib(searchBib);
    })
    .filter(Boolean);
  if (displayBibs.length === 0) {
    return null;
  }

  return {
    displayBibs,
    hasHistoryRoutingForTitles: hasHistoryRoutingForBibTitles,
    title,
    titleUrl: titleUrl.toString().replace(/\+/g, '%20')
  };
};

const mapLinksElementConfigToLinksRowProps = linksConfig => {
  const { links = [], title = '' } = linksConfig;
  if (!links.length) {
    return null;
  }

  const mappedLinks = links
    .map(link => {
      const { title: linkTitle, url } = link;
      const mappedUrl = mapLinkUrl(url);
      if (!mappedUrl) {
        return null;
      }
      return {
        title: linkTitle,
        url: mappedUrl
      };
    })
    .filter(Boolean);

  return {
    links: mappedLinks,
    title
  };
};

const mapListBibElementConfigToBibsRowProps = (
  listBibConfig,
  { hasHistoryRoutingForTitles = false, listById = {}, searchBibByMetadataId }
) => {
  const { metadataIds = [], rowContentIds = [] } = listBibConfig;
  if (!(metadataIds.length && rowContentIds.length)) {
    return null;
  }

  const list = listById[rowContentIds[0]];
  if (!list) {
    return null;
  }

  const displayBibs = metadataIds
    .map(metadataId => {
      const searchBib = searchBibByMetadataId[metadataId];
      if (!searchBib) {
        return null;
      }

      return mapSearchBibToDisplayBib(searchBib);
    })
    .filter(Boolean);
  if (!displayBibs.length) {
    return null;
  }

  const title = list.name;
  const titleUrl = `/list/share/${list.owner}/${list.id}`;

  return {
    displayBibs,
    hasHistoryRoutingForTitles,
    title,
    titleUrl
  };
};

const mapListCardsElementConfigToListsRowProps = (listsConfig, { listById = {}, userById = {} }) => {
  const { rowContentIds = [], title = '' } = listsConfig;

  const lists = (rowContentIds ?? [])
    .map(listId => {
      return listById[listId];
    })
    .filter(Boolean);
  if (!lists.length) {
    return null;
  }

  const userByListId = rowContentIds.reduce((acc, listId) => {
    const list = listById[listId];
    if (!list) {
      return acc;
    }

    const creatorUserId = list.owner;

    acc[listId] = userById[String(creatorUserId)];

    return acc;
  }, {});

  const listsWithUsers = lists.filter(list => Boolean(userByListId[list.id]));
  if (!listsWithUsers.length) {
    return null;
  }

  return {
    lists,
    title,
    userByListId
  };
};

const mapListHeroElementConfigToListsHeroRowProps = (listHeroConfig, { listById = {}, userById = {} }) => {
  const { rowContentIds = [], title = '' } = listHeroConfig;

  const lists = (rowContentIds ?? [])
    .map(listId => {
      return listById[listId];
    })
    .filter(Boolean);
  if (!lists.length) {
    return null;
  }

  const userByListId = rowContentIds.reduce((acc, listId) => {
    const list = listById[listId];
    if (!list) {
      return acc;
    }

    const creatorUserId = list.owner;

    acc[listId] = userById[String(creatorUserId)];

    return acc;
  }, {});

  const listsWithUsers = lists.filter(list => Boolean(userByListId[list.id]));
  if (!listsWithUsers.length) {
    return null;
  }

  return {
    lists,
    title,
    userByListId
  };
};

const mapNavElementConfigToNavRowProps = (navConfig, { hasHistoryRouting = false }) => {
  const { links = [] } = navConfig;

  const mappedLinks = (links ?? [])
    .map(link => {
      const { title: linkTitle, url } = link;
      const mappedUrl = mapLinkUrl(url);
      if (!mappedUrl) {
        return null;
      }
      return { isRouterLink: hasHistoryRouting, title: linkTitle, url: mappedUrl };
    })
    .filter(Boolean);
  if (!mappedLinks.length) {
    return null;
  }

  return {
    links: mappedLinks
  };
};

const getChildComponent = (
  browseConfigElement,
  { hasHistoryRoutingForBibTitles, hasHistoryRoutingForNavLinks, listById, searchBibByMetadataId, userById }
) => {
  const { config: elementConfig, type: elementType } = browseConfigElement;

  switch (elementType) {
    case BROWSE_CONFIG_ELEMENT_TYPE.BIB:
      return {
        mappedProps: mapBibElementConfigToBibsRowProps(elementConfig, {
          hasHistoryRoutingForBibTitles,
          searchBibByMetadataId
        }),
        type: ROW_CHILD_COMPONENT_TYPE.BIBS
      };
    case BROWSE_CONFIG_ELEMENT_TYPE.LIST_BIB:
      return {
        mappedProps: mapListBibElementConfigToBibsRowProps(elementConfig, {
          hasHistoryRoutingForTitles: hasHistoryRoutingForBibTitles,
          listById,
          searchBibByMetadataId
        }),
        type: ROW_CHILD_COMPONENT_TYPE.BIBS
      };
    case BROWSE_CONFIG_ELEMENT_TYPE.LINK:
      return {
        mappedProps: mapLinksElementConfigToLinksRowProps(elementConfig),
        type: ROW_CHILD_COMPONENT_TYPE.LINKS
      };
    case BROWSE_CONFIG_ELEMENT_TYPE.LIST_CARD:
      return {
        mappedProps: mapListCardsElementConfigToListsRowProps(elementConfig, {
          listById,
          userById
        }),
        type: ROW_CHILD_COMPONENT_TYPE.LISTS
      };
    case BROWSE_CONFIG_ELEMENT_TYPE.LIST_HERO:
      return {
        mappedProps: mapListHeroElementConfigToListsHeroRowProps(elementConfig, {
          listById,
          userById
        }),
        type: ROW_CHILD_COMPONENT_TYPE.LISTS_HERO
      };
    case BROWSE_CONFIG_ELEMENT_TYPE.NAV:
      return {
        mappedProps: mapNavElementConfigToNavRowProps(elementConfig, {
          hasHistoryRouting: hasHistoryRoutingForNavLinks
        }),
        type: ROW_CHILD_COMPONENT_TYPE.NAV
      };
    default: {
      /* eslint-disable-next-line no-console */
      console.error(`Unknown browse config element type: ${elementType}`);
      return null;
    }
  }
};

const sanitizeElement = rawElement => {
  const clonedElement = cloneDeep(rawElement);

  // replace the deprecated "LIST" type with a "LIST_HERO" type
  if (clonedElement.type === DEPRECATED_LIST_ELEMENT_CONFIG_TYPE) {
    clonedElement.type = BROWSE_CONFIG_ELEMENT_TYPE.LIST_HERO;
  }

  // Sometimes the title is in the element and sometimes in the config.
  // Preferably it would always be in the config.
  // For backwards compatibility, we are going to copy it
  // from the element to the config.
  if (clonedElement.title && !clonedElement.config.title) {
    clonedElement.config.title = clonedElement.title;
    delete clonedElement.title;
  }

  return clonedElement;
};

const mapBrowseConfigElementsToRows = (
  browseConfigElements,
  {
    hasHistoryRoutingForBibTitles,
    hasHistoryRoutingForNavLinks,
    listById,
    makeRowId = rowComponentType => uniqueId(`${rowComponentType}_`),
    searchBibByMetadataId,
    userById
  }
) => {
  if (!browseConfigElements.length) {
    return [];
  }

  return browseConfigElements
    .map(element => {
      const sanitizedElement = sanitizeElement(element);
      const { config: elementConfig, type: elementType } = sanitizedElement;
      if (!(elementConfig && elementType)) {
        return null;
      }

      const hasAlternativeStyle = Boolean(elementConfig.alternateRowStyle);
      const uiContainerType = UI_CONTAINER_TYPE_BY_BROWSE_CONFIG_ELEMENT_TYPE[elementType];
      const childComponent = getChildComponent(sanitizedElement, {
        hasHistoryRoutingForBibTitles,
        hasHistoryRoutingForNavLinks,
        listById,
        searchBibByMetadataId,
        userById
      });
      if (!(childComponent && childComponent.mappedProps)) {
        return null;
      }

      return {
        additionalAnalyticsPayload: {
          uiContainerType
        },
        hasAlternativeStyle,
        childComponent,
        rowId: makeRowId(childComponent.type)
      };
    })
    .filter(Boolean);
};

export { mapBrowseConfigElementsToRows };
